import { createContext, useContext, useEffect, useRef, useState } from "react";
import { mediaUrl } from "@/lib/api";

const Ctx = createContext(null);
export const usePlayer = () => useContext(Ctx);

const CAP = 60;

export const PlayerProvider = ({ children }) => {
  const audioRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const [track, setTrack] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [dur, setDur] = useState(CAP);
  const [modalOpen, setModalOpen] = useState(false);

  const ensureAnalyser = () => {
    if (analyserRef.current || !audioRef.current) return;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const actx = new AC();
      const src = actx.createMediaElementSource(audioRef.current);
      const an = actx.createAnalyser();
      an.fftSize = 256;
      an.smoothingTimeConstant = 0.82;
      src.connect(an);
      an.connect(actx.destination);
      audioCtxRef.current = actx;
      analyserRef.current = an;
    } catch {
      // visualiser unavailable — playback continues unaffected
    }
  };

  useEffect(() => {
    const a = new Audio();
    a.preload = "auto";
    audioRef.current = a;
    const onTime = () => {
      setTime(a.currentTime);
      if (a.currentTime >= CAP) {
        a.pause();
        setPlaying(false);
      }
    };
    const onMeta = () => setDur(Math.min(CAP, a.duration || CAP));
    const onEnd = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    return () => {
      a.pause();
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
    };
  }, []);

  useEffect(() => {
    const a = audioRef.current;
    if (!a || !track) return;
    if (!track.preview_url) {
      setPlaying(false);
      return;
    }
    a.src = mediaUrl(track.preview_url);
    a.currentTime = 0;
    setTime(0);
    ensureAnalyser();
    audioCtxRef.current?.resume?.();
    a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  }, [track]);

  const playTrack = (t) => {
    if (track && track.id === t.id) {
      toggle();
      return;
    }
    setTrack(t);
  };

  const toggle = () => {
    const a = audioRef.current;
    if (!a || !track) return;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      ensureAnalyser();
      audioCtxRef.current?.resume?.();
      a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  };

  const seek = (v) => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = v;
    setTime(v);
  };

  const close = () => {
    const a = audioRef.current;
    if (a) a.pause();
    setPlaying(false);
    setTrack(null);
    setModalOpen(false);
  };

  const openDetails = (t) => {
    if (!track || track.id !== t.id) setTrack(t);
    setModalOpen(true);
  };

  return (
    <Ctx.Provider
      value={{ track, playing, time, dur, modalOpen, setModalOpen, playTrack, openDetails, toggle, seek, close, analyserRef }}
    >
      {children}
    </Ctx.Provider>
  );
};

export const fmtTime = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
